import { useEffect, useRef, useState } from 'react';
import { Play, Pause, SpeakerHigh, SpeakerSlash, ArrowsOut, Circle, Warning } from '@phosphor-icons/react';

// Dynamic import for flv.js to handle module loading issues
let flvjs = null;

/**
 * FlvPlayer - Custom FLV video player for Tracksolid live streams
 * 
 * Uses flv.js to play WebSocket FLV streams from Tracksolid API
 */
const FlvPlayer = ({ 
  url, 
  className = '', 
  onError,
  onLoading,
  autoPlay = true,
  showControls = true 
}) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [flvLoaded, setFlvLoaded] = useState(false);

  // Load flv.js dynamically
  useEffect(() => {
    const loadFlvJs = async () => {
      try {
        if (!flvjs) {
          const module = await import('flv.js');
          flvjs = module.default || module;
        }
        setFlvLoaded(true);
      } catch (err) {
        console.error('Failed to load flv.js:', err);
        setError('Error cargando reproductor FLV');
        onError?.('Error cargando reproductor FLV');
      }
    };
    loadFlvJs();
  }, [onError]);

  useEffect(() => {
    if (!url || !videoRef.current || !flvLoaded || !flvjs) return;

    // Check flv.js support
    if (!flvjs.isSupported()) {
      const errMsg = 'Reproducción FLV no soportada en este navegador';
      setError(errMsg);
      onError?.(errMsg);
      return;
    }

    setIsLoading(true);
    setError(null);
    onLoading?.(true);

    // Clean up previous player
    if (playerRef.current) {
      try {
        playerRef.current.pause();
        playerRef.current.unload();
        playerRef.current.detachMediaElement();
        playerRef.current.destroy();
      } catch (e) {
        console.warn('Cleanup error:', e);
      }
      playerRef.current = null;
    }

    try {
      // Create flv.js player
      const player = flvjs.createPlayer({
        type: 'flv',
        url: url,
        isLive: true,
        hasAudio: true,
        hasVideo: true,
        cors: true,
      }, {
        enableWorker: false, // Disable worker for compatibility
        enableStashBuffer: false,
        stashInitialSize: 128,
        autoCleanupSourceBuffer: true,
        autoCleanupMaxBackwardDuration: 3,
        autoCleanupMinBackwardDuration: 1,
      });

      player.attachMediaElement(videoRef.current);
      player.load();

      // Event handlers
      player.on(flvjs.Events.ERROR, (errorType, errorDetail, errorInfo) => {
        console.error('FLV Error:', errorType, errorDetail, errorInfo);
        const errMsg = `Error: ${errorDetail || errorType}`;
        setError(errMsg);
        setIsLoading(false);
        onError?.(errMsg);
        onLoading?.(false);
      });

      player.on(flvjs.Events.LOADING_COMPLETE, () => {
        console.log('FLV Loading complete');
      });

      player.on(flvjs.Events.METADATA_ARRIVED, () => {
        console.log('FLV Metadata arrived');
        setIsLoading(false);
        onLoading?.(false);
      });

      // Auto-play
      if (autoPlay) {
        const playPromise = player.play();
        if (playPromise && playPromise.then) {
          playPromise.then(() => {
            setIsPlaying(true);
            setIsLoading(false);
            onLoading?.(false);
          }).catch(err => {
            console.warn('Autoplay blocked:', err);
            setIsLoading(false);
            onLoading?.(false);
          });
        }
      }

      playerRef.current = player;

      // Video element events
      const video = videoRef.current;
      
      const handlePlaying = () => {
        setIsPlaying(true);
        setIsLoading(false);
        onLoading?.(false);
      };
      
      const handlePause = () => setIsPlaying(false);
      const handleWaiting = () => setIsLoading(true);
      const handleCanPlay = () => {
        setIsLoading(false);
        onLoading?.(false);
      };

      video.addEventListener('playing', handlePlaying);
      video.addEventListener('pause', handlePause);
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('canplay', handleCanPlay);

      // Cleanup
      return () => {
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('canplay', handleCanPlay);
      };

    } catch (err) {
      console.error('FLV Player creation error:', err);
      setError(`Error al crear reproductor: ${err.message}`);
      setIsLoading(false);
      onError?.(`Error al crear reproductor: ${err.message}`);
      onLoading?.(false);
    }

    // Cleanup on unmount
    return () => {
      if (playerRef.current) {
        try {
          playerRef.current.pause();
          playerRef.current.unload();
          playerRef.current.detachMediaElement();
          playerRef.current.destroy();
        } catch (e) {
          console.warn('Cleanup error:', e);
        }
        playerRef.current = null;
      }
    };
  }, [url, autoPlay, onError, onLoading, flvLoaded]);

  const togglePlay = () => {
    if (!videoRef.current || !playerRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      playerRef.current.play();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const toggleFullscreen = async () => {
    const container = videoRef.current?.parentElement;
    if (!container) return;

    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.warn('Fullscreen error:', err);
    }
  };

  return (
    <div className={`relative bg-black rounded-lg overflow-hidden ${className}`}>
      {/* Video element */}
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        muted={isMuted}
        playsInline
        autoPlay={autoPlay}
      />

      {/* Loading overlay */}
      {isLoading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-2">
            <Circle size={40} className="animate-spin text-white" />
            <span className="text-white text-sm">Conectando stream...</span>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center gap-2 text-center p-4">
            <Warning size={40} className="text-red-500" />
            <span className="text-white text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Live badge */}
      {isPlaying && !error && (
        <div className="absolute top-3 left-3 flex items-center gap-2 px-2 py-1 bg-red-600 rounded text-white text-xs font-semibold">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          EN VIVO
        </div>
      )}

      {/* Controls */}
      {showControls && !error && (
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent opacity-0 hover:opacity-100 transition-opacity">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={togglePlay}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title={isPlaying ? 'Pausar' : 'Reproducir'}
              >
                {isPlaying ? (
                  <Pause size={20} className="text-white" weight="fill" />
                ) : (
                  <Play size={20} className="text-white" weight="fill" />
                )}
              </button>
              <button
                onClick={toggleMute}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                title={isMuted ? 'Activar audio' : 'Silenciar'}
              >
                {isMuted ? (
                  <SpeakerSlash size={20} className="text-white" />
                ) : (
                  <SpeakerHigh size={20} className="text-white" />
                )}
              </button>
            </div>
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              title="Pantalla completa"
            >
              <ArrowsOut size={20} className="text-white" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlvPlayer;
